import { PopperContent } from '@radix-ui/react-popper'
import { buttonReset } from 'components/Button/Button.styles'
import { Text } from 'components/Text'
import { styled } from 'stitches.config'

export const SelectList = styled('ul', {
  maxHeight: '240px',
  overflow: 'auto',

  backgroundColor: '$accent-white',
  borderRadius: '$1',

  px: '$1',
  listStyle: 'none',
  display: 'none',

  scrollbarWidth: 'none',

  '& .virtual-list': {
    scrollbarWidth: 'none',
  },

  '&::-webkit-scrollbar, & .virtual-list::-webkit-scrollbar': {
    width: 0,
  },

  variants: {
    isOpen: {
      true: {
        display: 'block',
        border: '1px solid $secondary-600',
      },
      false: {},
    },
  },
})

export const Option = styled('li', {
  padding: '$2',
  borderRadius: '$1',
  cursor: 'pointer',
  color: '$tertiary-600',
  fontSize: '$small',
  lineHeight: '$small',

  variants: {
    selected: {
      true: {
        backgroundColor: '$tertiary-200',
        fontWeight: '$medium',
      },
    },
    highlighted: {
      true: {
        backgroundColor: '$tertiary-100',
        fontWeight: '$medium',
      },
    },
  },
})

export const PopperList = styled(PopperContent, {
  [`&[data-side="top"] ${SelectList}`]: {
    borderBottomLeftRadius: '0px',
    borderBottomRightRadius: '0px',
    borderBottom: 'none',
    paddingTop: '$3',
  },
  [`&[data-side="bottom"] ${SelectList}`]: {
    borderTopLeftRadius: '0px',
    borderTopRightRadius: '0px',
    borderTop: 'none',
    paddingBottom: '$3',
  },
})

export const Content = styled('div', {
  fontSize: '$body',
  lineHeight: '$body',
  paddingRight: '$6',
  variants: {
    displayLabel: {
      true: {
        alignSelf: 'flex-end',
      },
      false: {
        alignSelf: 'center',
      },
    },
  },
})

export const SelectButton = styled('button', {
  ...buttonReset,
  paddingLeft: '$2',
  alignItems: 'center',
  backgroundColor: '$accent-white',
  border: '1px solid $secondary-600',
  borderRadius: '$3',
  cursor: 'pointer',
  display: 'inline-flex',
  fontSize: '$caption',
  justifyContent: 'flex-start',
  minWidth: '200px',
  position: 'relative',
  textAlign: 'center',
  width: '100%',
  transition: 'background-color 0.1s linear',

  '& .select-label': {
    top: '50%',
    color: '$tertiary-600',
    transition: 'font-size 0.1s ease, top ease 0.2s',
  },

  '&:disabled': {
    cursor: 'not-allowed',
  },

  '&:hover': {
    backgroundColor: '$tertiary-100',
  },

  variants: {
    isOpen: {
      true: {},
    },
    isFilled: {
      true: {
        '& .select-label': {
          position: 'absolute',
          top: '4px',
          fontSize: '$caption',
          color: '$tertiary-500',
        },
      },
    },
    size: {
      small: {
        height: '$4',
      },
      regular: {
        height: '$6',
      },
    },
    side: {
      bottom: {},
      top: {},
    },
  },
  compoundVariants: [
    {
      side: 'bottom',
      isOpen: true,
      css: {
        borderBottomColor: '$accent-white',
        borderBottomLeftRadius: '0px',
        borderBottomRightRadius: '0px',
      },
    },
    {
      side: 'top',
      isOpen: true,
      css: {
        borderTopColor: '$accent-white',
        borderTopLeftRadius: '0px',
        borderTopRightRadius: '0px',
      },
    },
  ],
  defaultVariants: {
    size: 'regular',
  },
})

export const IconContainer = styled('div', {
  cursor: 'pointer',
  position: 'absolute',
  right: '$2',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: '$primary-800',

  '& svg': {
    transition: 'all 0.1s linear',
  },
})

export const HelperText = styled(Text, {
  display: 'block',
  textAlign: 'left',
  color: '$secondary-800',
  fontSize: '$caption',
  marginTop: '4px',
  paddingLeft: '$2',
})

export const SelectRoot = styled('div', {
  position: 'relative',

  variants: {
    error: {
      true: {
        [`& ${Content}, & .select-label`]: {
          color: 'rgb($tag-red)',
        },
        [`& ${SelectButton}`]: {
          borderColor: 'rgb($tag-red)',
          cursor: 'not-allowed',
        },
        [`& ${IconContainer}`]: {
          color: '$primary-600',
        },
        [`& ${HelperText}`]: {
          color: 'rgb($tag-red)',
        },
      },
    },
    success: {
      true: {},
    },
    disabled: {
      true: {
        [`& ${Content}, & .select-label`]: {
          color: '$tertiary-300',
        },
        [`& ${SelectButton}`]: {
          borderColor: '$tertiary-100',
          cursor: 'not-allowed',
        },
        [`& ${IconContainer}`]: {
          color: '$tertiary-100',
        },
        [`& ${HelperText}`]: {
          color: '$tertiary-100',
        },
      },
    },
    isOpen: {
      true: {
        [`& ${SelectButton}`]: {
          '&:hover': {
            backgroundColor: '$accent-white',
          },
        },

        [`& ${IconContainer} svg`]: {
          transform: 'rotate(-180deg)',
        },
      },
      false: {},
    },
    isShort: {
      true: {
        '& button': {
          minWidth: '100px',
        },
        '@bp2': {
          '& button': {
            minWidth: '200px',
          },
        },
      },
    },
  },

  compoundVariants: [
    {
      error: true,
      isOpen: true,
      css: {
        [`& ${SelectButton}`]: {
          borderColor: 'rgb($tag-red)',
          borderBottomColor: '$accent-white',
        },
      },
    },
  ],
})
